<script lang="ts">
  import { useForm, router } from "@inertiajs/svelte";
  import { 
    LayoutGrid, Globe, Palette, BarChart3, 
    ArrowLeft, Save, Languages, Search 
  } from "lucide-svelte";
  import { toast } from "svelte-sonner";

  let { site = {} } = $props();

  const form = useForm({
    name: site.name || "",
    subdomain: site.subdomain || "",
    custom_domain: site.custom_domain || "",
    default_locale: site.default_locale || "zh",
    supported_locales: site.supported_locales || ["zh"],
    google_analytics_id: site.google_analytics_id || "",
    // 处理 JSON 嵌套
    appearance: {
      primary_color: site.appearance?.primary_color || "#3b82f6",
      theme: site.appearance?.theme || "light"
    },
    seo_metadata: {
      description: site.seo_metadata?.description || "",
      keywords: site.seo_metadata?.keywords || ""
    }
  });

  const localeOptions = [
    { label: "简体中文", value: "zh" },
    { label: "English", value: "en" },
    { label: "Deutsch", value: "de" },
    { label: "日本語", value: "ja" }
  ];

  function handleSubmit() {
    const action = site.id ? `/sites/${site.id}` : "/sites";
    const method = site.id ? "put" : "post";

    form[method](action, {
      onSuccess: () => toast.success("站点信息同步完成"),
      onError: () => toast.error("请检查输入信息")
    });
  }
</script>

<div class="min-h-screen bg-[#09090b] text-zinc-300">
  <header class="h-14 border-b border-zinc-800 bg-black flex items-center justify-between px-6 sticky top-0 z-50">
    <div class="flex items-center gap-4">
      <button onclick={() => router.get('/sites')} class="p-2 hover:bg-zinc-900 rounded-lg">
        <ArrowLeft class="size-4" />
      </button>
      <h1 class="text-sm font-bold text-white">{site.id ? '站点设置' : '新建站点资产'}</h1>
    </div>
    <button 
      onclick={handleSubmit} 
      disabled={form.processing}
      class="bg-blue-600 hover:bg-blue-500 text-white px-6 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-2"
    >
      <Save class="size-3" /> {form.processing ? '同步中...' : '保存配置'}
    </button>
  </header>

  <main class="max-w-5xl mx-auto p-8 grid grid-cols-12 gap-8">
    <div class="col-span-7 space-y-6">
      <section class="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 space-y-6">
        <div class="flex items-center gap-2 text-zinc-100 font-bold text-sm border-b border-zinc-800 pb-4">
          <LayoutGrid class="size-4 text-blue-500" /> 基础身份
        </div>
        
        <div class="grid grid-cols-2 gap-6">
          <div class="space-y-2">
            <label class="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">站点显示名称</label>
            <input bind:value={form.name} class="w-full bg-black border border-zinc-800 rounded-xl p-3 text-sm focus:ring-1 focus:ring-blue-500" placeholder="如：辰鑫口腔" />
          </div>
          <div class="space-y-2">
            <label class="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">系统识别码 (Subdomain)</label>
            <div class="flex items-center bg-black border border-zinc-800 rounded-xl px-3 group focus-within:ring-1 focus-within:ring-blue-500">
              <input bind:value={form.subdomain} class="bg-transparent border-none p-3 text-sm flex-1 outline-none" placeholder="chenxin" />
              <span class="text-zinc-600 text-xs">.fenti.me</span>
            </div>
          </div>
        </div>

        <div class="space-y-2">
          <label class="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">独立域名 (Custom Domain)</label>
          <div class="flex items-center bg-black border border-zinc-800 rounded-xl px-3">
            <Globe class="size-3 text-zinc-600 mr-2" />
            <input bind:value={form.custom_domain} class="bg-transparent border-none p-3 text-sm flex-1" placeholder="www.chenxinkouqiang.com" />
          </div>
        </div>
      </section>

      <section class="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 space-y-6">
        <div class="flex items-center gap-2 text-zinc-100 font-bold text-sm border-b border-zinc-800 pb-4">
          <Palette class="size-4 text-purple-500" /> 品牌外观
        </div>
        
        <div class="flex items-center gap-8">
          <div class="space-y-2">
            <label class="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">品牌主色 (Primary)</label>
            <div class="flex items-center gap-3">
              <input type="color" bind:value={form.appearance.primary_color} class="size-10 bg-transparent border-none cursor-pointer" />
              <input bind:value={form.appearance.primary_color} class="bg-black border border-zinc-800 rounded-lg p-2 font-mono text-xs w-24" />
            </div>
          </div>

          <div class="space-y-2">
            <label class="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">默认主题</label>
            <div class="flex bg-black border border-zinc-800 p-1 rounded-lg">
              {#each ['light', 'dark'] as t}
                <button 
                  onclick={() => form.appearance.theme = t}
                  class="px-4 py-1.5 text-[10px] font-bold rounded-md transition-all 
                  {form.appearance.theme === t ? 'bg-zinc-800 text-white' : 'text-zinc-600 hover:text-zinc-400'}"
                >
                  {t.toUpperCase()}
                </button>
              {/each}
            </div>
          </div>
        </div>
      </section>
    </div>

    <div class="col-span-5 space-y-6">
      <section class="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 space-y-6">
        <div class="flex items-center gap-2 text-zinc-100 font-bold text-sm border-b border-zinc-800 pb-4">
          <Languages class="size-4 text-emerald-500" /> 国际化配置
        </div>
        
        <div class="space-y-4">
          <div class="space-y-2">
            <label class="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">默认语言</label>
            <select bind:value={form.default_locale} class="w-full bg-black border border-zinc-800 rounded-xl p-3 text-sm appearance-none">
              {#each localeOptions as opt}
                <option value={opt.value}>{opt.label}</option>
              {/each}
            </select>
          </div>

          <div class="space-y-3">
            <label class="text-[10px] font-bold text-zinc-500 uppercase tracking-widest block">支持的语言列表</label>
            <div class="grid grid-cols-2 gap-2">
              {#each localeOptions as opt}
                <label class="flex items-center gap-2 p-2 bg-black/50 border border-zinc-800 rounded-lg cursor-pointer hover:border-zinc-700 transition-colors">
                  <input type="checkbox" value={opt.value} bind:group={form.supported_locales} class="size-3 rounded border-zinc-800 bg-zinc-900 text-blue-600" />
                  <span class="text-xs">{opt.label}</span>
                </label>
              {/each}
            </div>
          </div>
        </div>
      </section>

      <section class="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 space-y-6">
        <div class="flex items-center gap-2 text-zinc-100 font-bold text-sm border-b border-zinc-800 pb-4">
          <Search class="size-4 text-amber-500" /> 全站 SEO
        </div>
        <div class="space-y-4">
          <div class="space-y-2">
            <label class="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">GA4 跟踪 ID</label>
            <div class="flex items-center bg-black border border-zinc-800 rounded-xl px-3">
              <BarChart3 class="size-3 text-zinc-600 mr-2" />
              <input bind:value={form.google_analytics_id} class="bg-transparent border-none p-3 text-sm flex-1 font-mono" placeholder="G-XXXXXXXX" />
            </div>
          </div>
          <div class="space-y-2">
            <label class="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">默认 Meta Description</label>
            <textarea bind:value={form.seo_metadata.description} class="w-full bg-black border border-zinc-800 rounded-xl p-3 text-xs h-24 resize-none leading-relaxed" placeholder="为您的站点编写一段通用的搜索描述..."></textarea>
          </div>
        </div>
      </section>
    </div>
  </main>
</div>

<style>
  input:focus, select:focus, textarea:focus { outline: none; }
</style>